import React, { useEffect, useMemo, useState } from 'react';
import AGTableWrapper from 'components/AGTable/AGTableWrapper';
import AGMessage from 'components/AGMessage/AGMessage';
import AGSimpleSelect, { SelectOption } from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import Loader from 'components/Loader/Loader';
import Button from 'components/Button/Button';
import CaretIcon from 'components/Icons/CaretIcon';
import Pagination from 'components/Pagination/Pagination';
import ArrowSquareOutIcon from 'components/Icons/ArrowSquareOutIcon';
import DownloadIcon from 'assets/icons/download.svg?react';
import WarningIcon from 'assets/icons/warningTriangle.svg?react';
import { tenantsParkingClient } from 'clients';
import { TENANT_INVOICES_PAGE_SIZE } from 'clients/TenantsParkingClient';
import { TenantInvoice, TenantInvoiceStatusFilter, TenantInvoicesResponse } from 'types';
import { convertCentsToDisplayPrice } from 'utils/currency';
import { ALL_TIME_MONTH_YEAR, monthOptionsFromLaunchDate } from 'utils/monthYear';
import {
    InvoiceDisplayStatus,
    deriveInvoiceDisplayStatus,
    getInvoiceAction,
    formatInvoiceDate,
    invoiceStatusLabel,
    parseMonthYearValue,
    TenantSummary,
} from './utils';
import styles from './TenantInvoices.module.css';

type TenantInvoicesProps = {
    tenant: TenantSummary;
    // 'MM:YYYY', or ALL_TIME_MONTH_YEAR.
    initialMonth: string;
    timeZone?: string;
    launchDate?: string;
};

const STATUS_OPTIONS: SelectOption[] = [
    { value: TenantInvoiceStatusFilter.All, label: 'All Statuses' },
    { value: TenantInvoiceStatusFilter.Open, label: 'Open' },
    { value: TenantInvoiceStatusFilter.Overdue, label: 'Overdue' },
    { value: TenantInvoiceStatusFilter.Paid, label: 'Paid' },
];

const statusBadgeClass: Record<InvoiceDisplayStatus, string> = {
    overdue: styles.statusOverdue,
    paid: styles.statusPaid,
    open: styles.statusOpen,
    other: styles.statusOther,
};

function pluralize(count: number, word: string): string {
    return `${count} ${word}${count === 1 ? '' : 's'}`;
}

const InvoiceActionLink = ({ invoice }: { invoice: TenantInvoice }) => {
    const invoiceAction = getInvoiceAction(invoice);
    if (!invoiceAction) return null;
    const isDownload = invoiceAction.action === 'download';
    return (
        <div className={styles.actions}>
            <a
                href={invoiceAction.url}
                target="_blank"
                rel="noreferrer"
                className={styles.actionLink}
                aria-label={`${isDownload ? 'Download PDF for' : 'View'} ${invoice.display_name}`}
                title={isDownload ? 'Download PDF' : 'View invoice'}
            >
                {isDownload ? <DownloadIcon /> : <ArrowSquareOutIcon />}
            </a>
        </div>
    );
};

const TenantInvoices = ({
    tenant: { uuid: tenantUuid, name: tenantName },
    initialMonth,
    timeZone,
    launchDate,
}: TenantInvoicesProps) => {
    const monthSelectOptions = useMemo(() => monthOptionsFromLaunchDate(launchDate), [launchDate]);
    const [month, setMonth] = useState(initialMonth);
    const [status, setStatus] = useState<TenantInvoiceStatusFilter>(TenantInvoiceStatusFilter.All);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [data, setData] = useState<TenantInvoicesResponse | null>(null);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState('');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setErrorMessage('');
        (async () => {
            try {
                const res = await tenantsParkingClient.getTenantInvoices({
                    tenantUuid,
                    page,
                    status,
                    ...parseMonthYearValue(month),
                });
                if (!cancelled) setData(res);
            } catch (e) {
                if (!cancelled) {
                    setErrorMessage(e instanceof Error ? e.message : 'Failed to load invoices.');
                    setData(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [tenantUuid, month, status, page]);

    const totalPages = useMemo(() => Math.ceil((data?.count ?? 0) / TENANT_INVOICES_PAGE_SIZE), [data?.count]);
    const invoices = data?.results ?? [];
    const overdueNotice = data?.overdue_notice;

    function handleMonthSelect(value: string) {
        setMonth(value);
        setPage(1);
    }

    function handleStatusSelect(value: string) {
        setStatus(value as TenantInvoiceStatusFilter);
        setPage(1);
    }

    function showAllOverdue() {
        setMonth(ALL_TIME_MONTH_YEAR);
        setStatus(TenantInvoiceStatusFilter.Overdue);
        setPage(1);
    }

    async function exportCSV() {
        setExporting(true);
        setExportError('');
        try {
            await tenantsParkingClient.downloadTenantInvoicesCSV({
                tenantUuid,
                status,
                ...parseMonthYearValue(month),
                tenantName,
            });
        } catch {
            setExportError('Failed to export invoices. Please try again.');
        } finally {
            setExporting(false);
        }
    }

    return (
        <div className={styles.body}>
            <div className={styles.toolbar}>
                <h2>All Invoices</h2>
                <AGSimpleSelect
                    selected={month}
                    onSelect={handleMonthSelect}
                    options={monthSelectOptions}
                    placeholder="Month"
                    className={styles.toolbarInput}
                    width="100%"
                    maxHeight="300px"
                />
                <AGSimpleSelect
                    selected={status}
                    onSelect={handleStatusSelect}
                    options={STATUS_OPTIONS}
                    placeholder="Status"
                    className={styles.toolbarInput}
                    width="100%"
                />
                <Button
                    color="info"
                    size="sm"
                    disabled={exporting}
                    onClick={exportCSV}
                    fitContent
                    aria-label="Export CSV"
                >
                    <DownloadIcon /> Export CSV
                </Button>
            </div>
            {!!exportError && <AGMessage color="error" title={exportError} className={styles.exportError} />}

            {data && (
                <div className={styles.cards}>
                    <div>
                        <p className={styles.cardLabel}>Open Invoices</p>
                        <p className={styles.cardValue}>{data.summary.open_invoices}</p>
                    </div>
                    <div>
                        <p className={styles.cardLabel}>Overdue Invoices</p>
                        <p className={styles.cardValue}>{data.summary.overdue_invoices}</p>
                    </div>
                    <div>
                        <p className={styles.cardLabel}>Overdue Amount</p>
                        <p className={styles.cardValue}>{convertCentsToDisplayPrice(data.summary.overdue_amount)}</p>
                    </div>
                </div>
            )}

            {!!overdueNotice?.count && (
                <AGMessage
                    color="error"
                    className={styles.overdueNotice}
                    role="button"
                    tabIndex={0}
                    onClick={showAllOverdue}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') showAllOverdue();
                    }}
                >
                    <WarningIcon className={styles.overdueIcon} />
                    <span className={styles.overdueText}>
                        {`${pluralize(overdueNotice.count, 'overdue invoice')} · ${convertCentsToDisplayPrice(
                            overdueNotice.amount_remaining
                        )} overdue`}
                    </span>
                    <CaretIcon direction="right" className={styles.overdueCaret} />
                </AGMessage>
            )}

            <h3 className={styles.sectionTitle}>Billing History</h3>
            {!!errorMessage && <AGMessage color="error" title={errorMessage} />}
            {loading && <Loader />}
            {!loading && !errorMessage && (
                <>
                    <div className={styles.tableScroll}>
                        <AGTableWrapper>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Due Date</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th>Paid Date</th>
                                    <th aria-label="Actions" />
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.length > 0 ? (
                                    invoices.map((invoice) => (
                                        <tr key={invoice.id}>
                                            <td>{invoice.display_name}</td>
                                            <td>
                                                {formatInvoiceDate(invoice.due_date ?? invoice.created_at, timeZone)}
                                            </td>
                                            <td>{convertCentsToDisplayPrice(invoice.amount_due)}</td>
                                            <td>
                                                <span
                                                    className={`${styles.statusTag} ${
                                                        statusBadgeClass[deriveInvoiceDisplayStatus(invoice)]
                                                    }`}
                                                >
                                                    {invoiceStatusLabel(invoice)}
                                                </span>
                                            </td>
                                            <td>{formatInvoiceDate(invoice.paid_at, timeZone)}</td>
                                            <td>
                                                <InvoiceActionLink invoice={invoice} />
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6}>No invoices for this period.</td>
                                    </tr>
                                )}
                            </tbody>
                            {totalPages > 1 && (
                                <tfoot>
                                    <tr>
                                        <td colSpan={6}>
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                <Pagination
                                                    currentPage={page}
                                                    totalPages={totalPages}
                                                    onPageChange={(p) => setPage(Number(p))}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </AGTableWrapper>
                    </div>
                </>
            )}
        </div>
    );
};

export default TenantInvoices;
